"use client"

import dynamic from "next/dynamic"
import Image from "next/image"
import Link from "next/link"
import clsx from "clsx"
import { SubtleText } from "@ui/text"

import { formatDuration } from "domains/utils/formatDuration"
import { formatDisplayDate } from "domains/utils/intl"
import type { PodcastEpisode } from "domains/podcasts"

interface Props {
    episodes: PodcastEpisode[]
}

const isRestrictedPlayback = (policy?: string | null) => policy === "signed" || policy === "drm"

function parsePublishDate(value: PodcastEpisode["publishDate"]): Date | null {
    if (!value) return null
    if (value instanceof Date) return value
    if (typeof value === "string") {
        const parsed = new Date(value)
        return Number.isNaN(parsed.getTime()) ? null : parsed
    }
    const parsed = new Date(value as Date)
    return Number.isNaN(parsed.getTime()) ? null : parsed
}

function PlayerSkeleton() {
    return (
        <div className="flex h-[120px] w-full flex-col justify-center gap-3">
            <div className="h-3 w-24 animate-pulse rounded-full bg-gray-200 dark:bg-zinc-800" />
            <div className="h-3 w-full animate-pulse rounded-full bg-gray-200 dark:bg-zinc-800" />
            <div className="h-3 w-3/4 animate-pulse rounded-full bg-gray-200 dark:bg-zinc-800" />
        </div>
    )
}

const PodcastMuxPlayer = dynamic(
    () => import("domains/podcasts/components/PodcastMuxPlayer").then((mod) => mod.PodcastMuxPlayer),
    {
        ssr: false,
        loading: () => <PlayerSkeleton />,
    }
)

export function MuxTalkingAiList({ episodes }: Props) {
    const entries = episodes
        .map((episode) => ({
            episode,
            publishDate: parsePublishDate(episode.publishDate),
        }))
        .filter((item): item is { episode: PodcastEpisode; publishDate: Date } => Boolean(item.publishDate))
        .sort((a, b) => b.publishDate.getTime() - a.publishDate.getTime())
        .slice(0, 4)

    if (entries.length === 0) {
        return null
    }

    return (
        <section className="space-y-6">
            <div className="flex items-center justify-between gap-4">
                <h2 className="text-xl font-semibold lg:text-2xl dark:text-gray-100">Talking AI Podcast</h2>
                <Link
                    href="/academy/talking-ai"
                    className="text-primary-700 dark:text-primary-300 text-sm font-semibold underline-offset-4 hover:underline"
                >
                    Browse all episodes
                </Link>
            </div>
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {entries.map(({ episode, publishDate }) => (
                    <PodcastListItem key={episode.slug} episode={episode} publishDate={publishDate} />
                ))}
            </ul>
        </section>
    )
}

function PodcastListItem({ episode, publishDate }: { episode: PodcastEpisode; publishDate: Date }) {
    const durationSeconds = episode.durationSeconds ?? null
    const durationLabel = durationSeconds ? formatDuration(durationSeconds) : null
    const dateLabel = formatDisplayDate(publishDate)
    const description = episode.description ?? episode.teaser ?? episode.showNotesPlain ?? ""
    const href = `/academy/videos/${episode.slug}`
    const thumbnailUrl = episode.images.square?.url ?? episode.images.default?.url ?? null
    const posterUrl = episode.images.square?.url ?? episode.images.wide?.url ?? episode.images.default?.url ?? null

    const playbackId = episode.audio.playbackId ?? null
    const playbackPolicy = episode.audio.playbackPolicy ?? null
    const canStream = Boolean(playbackId) && !isRestrictedPlayback(playbackPolicy)

    return (
        <li className="flex flex-col gap-6 py-6 first:pt-0 last:pb-0 md:flex-row md:items-start md:gap-6">
            <div className="flex flex-1 gap-4">
                <Thumbnail title={episode.title} url={thumbnailUrl} />
                <div className="flex flex-1 flex-col gap-2">
                    <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                        <span>Podcast episode</span>
                        <span aria-hidden="true">•</span>
                        <span>{dateLabel}</span>
                        {durationLabel && (
                            <>
                                <span aria-hidden="true">•</span>
                                <span>{durationLabel}</span>
                            </>
                        )}
                    </div>
                    <Link
                        href={href}
                        className="text-lg leading-tight font-semibold text-gray-900 hover:underline dark:text-gray-100"
                    >
                        {episode.title}
                    </Link>
                    {description && (
                        <SubtleText className="text-sm text-gray-600 dark:text-gray-300">{description}</SubtleText>
                    )}
                </div>
            </div>
            {canStream ? (
                <PlayerFrame playbackId={playbackId!} posterUrl={posterUrl} title={episode.title} />
            ) : (
                <div className="4xl:flex relative hidden w-full max-w-[360px] justify-end">
                    <div className="rounded-3xl border border-yellow-400 bg-yellow-50 p-4 text-sm text-yellow-900 dark:border-yellow-600 dark:bg-yellow-950 dark:text-yellow-200">
                        Audio preview is not available for this episode.
                    </div>
                </div>
            )}
        </li>
    )
}

function Thumbnail({ title, url }: { title: string; url: string | null }) {
    return (
        <div className="relative h-32 w-32 overflow-hidden rounded-md bg-gray-200 dark:bg-zinc-800">
            {url ? (
                <Image src={url} alt={title} fill sizes="128px" className="object-cover" />
            ) : (
                <div className="flex h-full w-full items-center justify-center text-xs font-medium text-gray-500 dark:text-gray-300">
                    AI
                </div>
            )}
        </div>
    )
}

function PlayerFrame({
    playbackId,
    posterUrl,
    title,
    className,
}: {
    playbackId: string
    posterUrl: string | null
    title: string
    className?: string
}) {
    return (
        <div
            className={clsx(
                "mt-0 h-10 max-w-[32rem] overflow-hidden bg-white pt-0 dark:border-gray-700 dark:bg-zinc-900",
                className
            )}
        >
            <PodcastMuxPlayer
                playbackId={playbackId}
                title={title}
                posterUrl={posterUrl ?? undefined}
                metadata={{
                    video_id: playbackId,
                    video_title: title,
                }}
            />
        </div>
    )
}

function StackedPlayer({
    playbackId,
    posterUrl,
    title,
}: {
    playbackId: string
    posterUrl: string | null
    title: string
}) {
    return (
        <div className="4xl:flex relative hidden w-full max-w-[360px] justify-end">
            <div className="absolute inset-0 translate-x-4 translate-y-4 rounded-3xl border border-gray-200 bg-white/60 dark:border-gray-700 dark:bg-zinc-900/60" />
            <div className="absolute inset-0 translate-x-2 translate-y-2 rounded-3xl border border-gray-200 bg-white/75 dark:border-gray-700 dark:bg-zinc-900/75" />
            <PlayerFrame playbackId={playbackId} posterUrl={posterUrl} title={title} className="relative w-full" />
        </div>
    )
}
